import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    Chip,
    CircularProgress,
    useTheme,
    alpha,
} from '@mui/material';
import { toast } from 'react-toastify';
import { WorkerCertification, CertReviewStatus } from '../../models/worker';
import { fetchWorkerCertifications, confirmCert, rejectCert } from '../../api/workerCertificationsApi';
import CertReviewDialog from '../CertReview/CertReviewDialog';
import socketService from '../../services/socketService';

interface WorkerCertReviewProps {
    workerNumericId: number;
}

const STATUS_META: Record<CertReviewStatus, { label: string; color: string }> = {
    [CertReviewStatus.VERIFIED]: { label: 'Verified', color: '#2e7d32' },
    [CertReviewStatus.PENDING_REVIEW]: { label: 'Pending review', color: '#ed6c02' },
    [CertReviewStatus.PENDING_EXTRACTION]: { label: 'Extracting…', color: '#0288d1' },
    [CertReviewStatus.REJECTED]: { label: 'Rejected', color: '#d32f2f' },
    [CertReviewStatus.MANUAL_UNVERIFIED]: { label: 'Manual (unverified)', color: '#757575' },
    [CertReviewStatus.REUPLOAD_REQUIRED]: { label: 'Re-upload required', color: '#ed6c02' },
};

/**
 * Per-worker certificate review: shows all of a worker's certificates with their
 * review status and lets the recruiter approve / reject / open the full review
 * dialog — the single-worker equivalent of the global cert-review queue.
 */
const WorkerCertReview: React.FC<WorkerCertReviewProps> = ({ workerNumericId }) => {
    const theme = useTheme();
    const [certs, setCerts] = useState<WorkerCertification[]>([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [reviewing, setReviewing] = useState<WorkerCertification | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const rows = await fetchWorkerCertifications(workerNumericId);
            setCerts(rows);
        } catch {
            toast.error('Failed to load certificates.');
        } finally {
            setLoading(false);
        }
    }, [workerNumericId]);

    useEffect(() => { load(); }, [load]);

    // Live-refresh when the cert-ingest pipeline finishes for THIS worker.
    // A "Reassess" on a certificate file (or a dashboard/WhatsApp upload) creates
    // the review rows asynchronously in a Bull worker, which emits these events on
    // completion. Without this the tab is stale until a full page refresh. Filter
    // by workerId so events for other workers/tabs are no-ops here.
    useEffect(() => {
        const onCompleted = (payload: { workerId: number }) => {
            if (payload.workerId === workerNumericId) load();
        };
        const onSourceUpdated = (payload: { workerId: number }) => {
            if (payload.workerId === workerNumericId) load();
        };
        const onReviewed = (payload: { workerId: number }) => {
            if (payload.workerId === workerNumericId) load();
        };
        socketService.onCertUploadCompleted(onCompleted);
        socketService.onCertSourceUpdated(onSourceUpdated);
        socketService.onCertReviewUpdated(onReviewed);
        return () => {
            socketService.offCertUploadCompleted(onCompleted);
            socketService.offCertSourceUpdated(onSourceUpdated);
            socketService.offCertReviewUpdated(onReviewed);
        };
    }, [workerNumericId, load]);

    const handleApprove = async (cert: WorkerCertification) => {
        setBusyId(cert.id);
        try {
            await confirmCert(cert.id);
            toast.success('Certificate approved.');
            await load();
        } catch {
            toast.error('Approve failed.');
        } finally {
            setBusyId(null);
        }
    };

    const handleReject = async (cert: WorkerCertification) => {
        const reason = window.prompt('Reason for rejecting this certificate?');
        if (!reason || !reason.trim()) return;
        setBusyId(cert.id);
        try {
            await rejectCert(cert.id, reason.trim());
            toast.success('Certificate rejected.');
            await load();
        } catch {
            toast.error('Reject failed.');
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (certs.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                No certificates uploaded for this worker yet.
            </Typography>
        );
    }

    const pendingCount = certs.filter((c) => c.status === CertReviewStatus.PENDING_REVIEW).length;

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={800}>Certificate Review</Typography>
                <Chip size="small" label={`${certs.length} total`} sx={{ height: 20, fontWeight: 700 }} />
                {pendingCount > 0 && (
                    <Chip size="small" color="warning" label={`${pendingCount} pending`} sx={{ height: 20, fontWeight: 700 }} />
                )}
            </Stack>

            <Stack spacing={1.5}>
                {certs.map((cert) => {
                    const meta = STATUS_META[cert.status] ?? { label: cert.status, color: theme.palette.text.secondary };
                    const isPending = cert.status === CertReviewStatus.PENDING_REVIEW;
                    const busy = busyId === cert.id;
                    return (
                        <Paper key={cert.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} flexWrap="wrap">
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700}>
                                        {cert.proposedCertName || 'Untitled certificate'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                        {[cert.issuer, cert.certCategory, cert.expiryDate ? `expires ${cert.expiryDate}` : null]
                                            .filter(Boolean).join(' · ') || '—'}
                                    </Typography>
                                    {cert.validationFlags && cert.validationFlags.length > 0 && (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                                            {cert.validationFlags.map((f, i) => (
                                                <Chip key={i} size="small" color="warning" variant="outlined"
                                                    label={typeof f === 'string' ? f : (f as any)?.code ?? 'flag'}
                                                    sx={{ height: 18, fontSize: '0.65rem' }} />
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                                <Chip
                                    size="small"
                                    label={meta.label}
                                    sx={{ bgcolor: alpha(meta.color, 0.12), color: meta.color, fontWeight: 600, height: 22, flexShrink: 0 }}
                                />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => setReviewing(cert)} disabled={busy}>
                                    Review
                                </Button>
                                {isPending && (
                                    <>
                                        <Button size="small" variant="outlined" color="error" onClick={() => handleReject(cert)} disabled={busy}>
                                            Reject
                                        </Button>
                                        <Button size="small" variant="contained" color="success" onClick={() => handleApprove(cert)} disabled={busy}>
                                            {busy ? 'Approving…' : 'Approve'}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            <CertReviewDialog
                open={!!reviewing}
                cert={reviewing}
                onClose={() => setReviewing(null)}
                onChanged={() => { setReviewing(null); load(); }}
            />
        </Box>
    );
};

export default WorkerCertReview;
